import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown, Clock, AlertCircle } from 'lucide-react';
import { db, appId } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

const CurrentPeriodIndicator = ({ user, onPeriodChange }) => {
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [allPeriods, setAllPeriods] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadCurrentPeriod();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadCurrentPeriod = async () => {
    try {
      setIsLoading(true);

      // First check if user has currentAcademicPeriod at user level
      if (user.currentAcademicPeriod) {
        const periodRef = doc(db, `artifacts/${appId}/academicPeriods`, user.currentAcademicPeriod);
        const periodSnap = await getDoc(periodRef);

        if (periodSnap.exists()) {
          const periodData = { id: periodSnap.id, ...periodSnap.data() };
          setCurrentPeriod(periodData);
          setIsLoading(false);
          return;
        } else {
          // Period document not found at user level
        }
      } else {
        // No period at user level, checking profile subcollection
      }

      // If not found at user level, check profile subcollection based on user role
      try {
        const userRole = user.role || 'teacher';

        if (userRole === 'student') {
          // For students, check student profile first
          const studentProfileRef = doc(db, `artifacts/${appId}/users/${user.uid}/profile`, 'siswa');
          const studentProfileSnap = await getDoc(studentProfileRef);

          if (studentProfileSnap.exists()) {
            const studentProfileData = studentProfileSnap.data();
            console.log('📅 CurrentPeriodIndicator: Student profile data:', studentProfileData);
            if (studentProfileData.currentAcademicPeriod) {
              const periodRef = doc(db, `artifacts/${appId}/academicPeriods`, studentProfileData.currentAcademicPeriod);
              const periodSnap = await getDoc(periodRef);

              if (periodSnap.exists()) {
                const periodData = { id: periodSnap.id, ...periodSnap.data() };
                setCurrentPeriod(periodData);
                console.log('📅 CurrentPeriodIndicator: Loaded from student profile:', periodData);
                setIsLoading(false);
                return;
              }
            }
          }
        } else {
          // For teachers, check guru profile
          const profileRef = doc(db, `artifacts/${appId}/users/${user.uid}/profile`, 'guru');
          const profileSnap = await getDoc(profileRef);

          if (profileSnap.exists()) {
            const profileData = profileSnap.data();
            console.log('📅 CurrentPeriodIndicator: Teacher profile data:', profileData);
            if (profileData.currentAcademicPeriod) {
              const periodRef = doc(db, `artifacts/${appId}/academicPeriods`, profileData.currentAcademicPeriod);
              const periodSnap = await getDoc(periodRef);

              if (periodSnap.exists()) {
                const periodData = { id: periodSnap.id, ...periodSnap.data() };
                setCurrentPeriod(periodData);
                console.log('📅 CurrentPeriodIndicator: Loaded from teacher profile:', periodData);
                setIsLoading(false);
                return;
              }
            }
          }
        }
      } catch (profileError) {
        console.log('📅 CurrentPeriodIndicator: Profile error:', profileError);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading current period:', error);
      setIsLoading(false);
    }
  };

  const formatPeriodDisplay = (period) => {
    if (!period) return 'Tidak ada periode';

    const semester = period.semester === 'ganjil' ? 'Ganjil' : 'Genap';
    const year = period.academicYear || 'Tahun tidak diketahui';

    return `${year} - ${semester}`;
  };

  const getPeriodStatus = (period) => {
    if (!period) return 'inactive';

    const now = new Date();
    const startDate = period.startDate?.toDate ? period.startDate.toDate() : new Date(period.startDate);
    const endDate = period.endDate?.toDate ? period.endDate.toDate() : new Date(period.endDate);

    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'ended';
    return 'active';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'upcoming': return 'text-blue-600 bg-blue-100';
      case 'ended': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'upcoming': return 'Akan datang';
      case 'ended': return 'Berakhir';
      default: return 'Tidak aktif';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 sm:px-3 py-2 bg-gray-100 rounded-lg">
        <Clock className="w-4 h-4 text-gray-500 animate-pulse flex-shrink-0" />
        <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">Memuat periode...</span>
        <span className="text-xs text-gray-500 sm:hidden">Loading...</span>
      </div>
    );
  }

  if (!currentPeriod) {
    return (
      <div className="flex items-center gap-2 px-2 sm:px-3 py-2 bg-yellow-100 rounded-lg">
        <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
        <span className="text-xs sm:text-sm text-yellow-700 hidden sm:inline">Belum ada periode</span>
        <span className="text-xs text-yellow-700 sm:hidden">No period</span>
      </div>
    );
  }

  const status = getPeriodStatus(currentPeriod);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg transition-colors hover:bg-gray-50 ${getStatusColor(status)}`}
      >
        <Calendar className="w-4 h-4 flex-shrink-0" />
        <div className="text-left hidden sm:block">
          <div className="text-sm font-medium">
            {formatPeriodDisplay(currentPeriod)}
          </div>
          <div className="text-xs opacity-75">
            {getStatusText(status)}
          </div>
        </div>
        <div className="text-left sm:hidden">
          <div className="text-xs font-medium">
            {formatPeriodDisplay(currentPeriod)}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-72 sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">Periode Akademik Saat Ini</h3>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Tahun Ajaran</span>
                  <span className="text-sm text-gray-600">{currentPeriod.academicYear}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Semester</span>
                  <span className="text-sm text-gray-600 capitalize">{currentPeriod.semester}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Status</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(status)}`}>
                    {getStatusText(status)}
                  </span>
                </div>
                {currentPeriod.startDate && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Mulai</span>
                    <span className="text-sm text-gray-600">
                      {currentPeriod.startDate.toDate ?
                        currentPeriod.startDate.toDate().toLocaleDateString('id-ID') :
                        new Date(currentPeriod.startDate).toLocaleDateString('id-ID')
                      }
                    </span>
                  </div>
                )}
                {currentPeriod.endDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Berakhir</span>
                    <span className="text-sm text-gray-600">
                      {currentPeriod.endDate.toDate ?
                        currentPeriod.endDate.toDate().toLocaleDateString('id-ID') :
                        new Date(currentPeriod.endDate).toLocaleDateString('id-ID')
                      }
                    </span>
                  </div>
                )}
              </div>

              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Untuk mengubah periode, buka halaman Profil
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentPeriodIndicator;
